import { getConfig } from './config';

function generateRandomString(length: number): string {
  const arr = new Uint8Array(length);
  crypto.getRandomValues(arr);
  return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

export async function signIn(): Promise<void> {
  const config = await getConfig();
  const codeVerifier = generateRandomString(64);
  sessionStorage.setItem('pkce_verifier', codeVerifier);
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  const url =
    `https://${config.cognitoDomain}/oauth2/authorize` +
    `?client_id=${config.clientId}` +
    `&response_type=code` +
    `&scope=openid+email` +
    `&redirect_uri=${encodeURIComponent(config.redirectUri)}` +
    `&identity_provider=AmazonFederate` +
    `&code_challenge_method=S256` +
    `&code_challenge=${codeChallenge}`;
  window.location.href = url;
}

async function exchangeCodeForTokens(code: string): Promise<{ id_token?: string } | null> {
  const config = await getConfig();
  const codeVerifier = sessionStorage.getItem('pkce_verifier');
  if (!codeVerifier) return null;

  const resp = await fetch(`https://${config.cognitoDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: config.clientId,
      code,
      redirect_uri: config.redirectUri,
      code_verifier: codeVerifier,
    }),
  });
  sessionStorage.removeItem('pkce_verifier');
  if (!resp.ok) return null;
  return resp.json();
}

function decodeJwt(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
}

export interface AuthState {
  idToken: string;
  email: string;
}

export async function initAuth(): Promise<AuthState | null> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  if (code) {
    const tokens = await exchangeCodeForTokens(code);
    if (tokens?.id_token) {
      sessionStorage.setItem('pist_id_token', tokens.id_token);
      window.history.replaceState(null, '', window.location.pathname);
    }
  }

  const idToken = sessionStorage.getItem('pist_id_token');
  if (idToken) {
    const claims = decodeJwt(idToken);
    if (claims && (claims.exp as number) * 1000 > Date.now()) {
      return {
        idToken,
        email: (claims.email as string) || (claims['cognito:username'] as string) || '',
      };
    }
    sessionStorage.removeItem('pist_id_token');
  }
  return null;
}
